export type Line = Array<number | null>

export const sma = (values: number[], period: number): Line => {
    const output: Line = []
    let total = 0

    for (let index = 0; index < values.length; index++) {
        total += values[index]
        if (index >= period) total -= values[index - period]
        output.push(index >= period - 1 ? total / period : null)
    }

    return output
}

export const ema = (values: number[], period: number): Line => {
    const output: Line = []
    const k = 2 / (period + 1)
    let previous: number | null = null
    let seed = 0

    for (let index = 0; index < values.length; index++) {
        if (index < period - 1) {
            seed += values[index]
            output.push(null)
            continue
        }

        if (previous === null) {
            seed += values[index]
            previous = seed / period
        }
        else {
            previous = values[index] * k + previous * (1 - k)
        }

        output.push(previous)
    }

    return output
}

export const rsi = (closes: number[], period = 14): Line => {
    const output: Line = [null]
    let gain = 0
    let loss = 0

    for (let index = 1; index < closes.length; index++) {
        const change = closes[index] - closes[index - 1]
        const up = change > 0 ? change : 0
        const down = change < 0 ? -change : 0

        if (index <= period) {
            gain += up
            loss += down

            if (index === period) {
                gain /= period
                loss /= period
                output.push(loss === 0 ? 100 : 100 - 100 / (1 + gain / loss))
            }
            else {
                output.push(null)
            }

            continue
        }

        gain = (gain * (period - 1) + up) / period
        loss = (loss * (period - 1) + down) / period
        output.push(loss === 0 ? 100 : 100 - 100 / (1 + gain / loss))
    }

    return output
}

export const macd = (closes: number[], fast = 12, slow = 26, signalPeriod = 9): { macd: Line, signal: Line, histogram: Line } => {
    const fastLine = ema(closes, fast)
    const slowLine = ema(closes, slow)
    const macdLine: Line = closes.map((_, index) => {
        const a = fastLine[index]
        const b = slowLine[index]

        return a === null || b === null ? null : a - b
    })

    const firstValid = macdLine.findIndex(value => value !== null)
    const signal: Line = closes.map(() => null)

    if (firstValid >= 0) {
        const compact = macdLine.slice(firstValid).map(value => value ?? 0)
        const signalCompact = ema(compact, signalPeriod)
        signalCompact.forEach((value, offset) => {
            signal[firstValid + offset] = value
        })
    }

    const histogram: Line = macdLine.map((value, index) => {
        const s = signal[index]

        return value === null || s === null ? null : value - s
    })

    return { macd: macdLine, signal, histogram }
}

export const bollinger = (closes: number[], period = 20, multiplier = 2): { middle: Line, upper: Line, lower: Line } => {
    const middle = sma(closes, period)
    const upper: Line = []
    const lower: Line = []

    for (let index = 0; index < closes.length; index++) {
        const average = middle[index]

        if (average === null) {
            upper.push(null)
            lower.push(null)
            continue
        }

        let total = 0
        for (let offset = index - period + 1; offset <= index; offset++) {
            const diff = closes[offset] - average
            total += diff * diff
        }

        const deviation = Math.sqrt(total / period)
        upper.push(average + multiplier * deviation)
        lower.push(average - multiplier * deviation)
    }

    return { middle, upper, lower }
}

export const atr = (highs: number[], lows: number[], closes: number[], period = 14): Line => {
    const output: Line = []
    let previous: number | null = null
    let seed = 0

    for (let index = 0; index < closes.length; index++) {
        const range = index === 0
            ? highs[index] - lows[index]
            : Math.max(highs[index] - lows[index], Math.abs(highs[index] - closes[index - 1]), Math.abs(lows[index] - closes[index - 1]))

        if (index < period - 1) {
            seed += range
            output.push(null)
            continue
        }

        if (previous === null) {
            seed += range
            previous = seed / period
        }
        else {
            previous = (previous * (period - 1) + range) / period
        }

        output.push(previous)
    }

    return output
}

export const moneyFlowIndex = (highs: number[], lows: number[], closes: number[], volumes: number[], period = 14): Line => {
    const output: Line = [null]
    const positive: number[] = [0]
    const negative: number[] = [0]

    for (let index = 1; index < closes.length; index++) {
        const typical = (highs[index] + lows[index] + closes[index]) / 3
        const previousTypical = (highs[index - 1] + lows[index - 1] + closes[index - 1]) / 3
        const flow = typical * volumes[index]

        positive.push(typical > previousTypical ? flow : 0)
        negative.push(typical < previousTypical ? flow : 0)

        if (index < period) {
            output.push(null)
            continue
        }

        let up = 0
        let down = 0
        for (let offset = index - period + 1; offset <= index; offset++) {
            up += positive[offset]
            down += negative[offset]
        }

        output.push(down === 0 ? 100 : 100 - 100 / (1 + up / down))
    }

    return output
}

export const onBalanceVolume = (closes: number[], volumes: number[]): number[] => {
    const output: number[] = []
    let total = 0

    for (let index = 0; index < closes.length; index++) {
        if (index > 0) {
            if (closes[index] > closes[index - 1]) total += volumes[index]
            else if (closes[index] < closes[index - 1]) total -= volumes[index]
        }

        output.push(total)
    }

    return output
}

export const stochastic = (highs: number[], lows: number[], closes: number[], period = 14, smooth = 3): { k: Line, d: Line } => {
    const k: Line = []

    for (let index = 0; index < closes.length; index++) {
        if (index < period - 1) {
            k.push(null)
            continue
        }

        let highest = -Infinity
        let lowest = Infinity
        for (let offset = index - period + 1; offset <= index; offset++) {
            if (highs[offset] > highest) highest = highs[offset]
            if (lows[offset] < lowest) lowest = lows[offset]
        }

        k.push(highest === lowest ? 50 : ((closes[index] - lowest) / (highest - lowest)) * 100)
    }

    const d: Line = k.map((_, index) => {
        if (index < period - 1 + smooth - 1) return null

        let total = 0
        for (let offset = index - smooth + 1; offset <= index; offset++) total += k[offset] ?? 0

        return total / smooth
    })

    return { k, d }
}

export const pivotPoints = (high: number, low: number, close: number): { pivot: number, r1: number, r2: number, r3: number, s1: number, s2: number, s3: number } => {
    const pivot = (high + low + close) / 3

    return {
        pivot,
        r1: 2 * pivot - low,
        r2: pivot + (high - low),
        r3: high + 2 * (pivot - low),
        s1: 2 * pivot - high,
        s2: pivot - (high - low),
        s3: low - 2 * (high - pivot)
    }
}

export const lastValue = (line: Line): number | null => {
    for (let index = line.length - 1; index >= 0; index--) {
        const value = line[index]
        if (value !== null) return value
    }

    return null
}
